import React, { useState, useEffect, useRef, useMemo } from "react";
import axios from "axios";
import { TbFireExtinguisher } from "react-icons/tb";
import { LuInfo, LuLock } from "react-icons/lu";
import { generateInspectionPDF } from "../generateInspectionPDF";
import { API, colors } from "../shared/constants";
import { loadTechSignatures } from "../shared/signatures";
import { styles, inputStyle } from "../shared/styles";
import { useIsMobile, naturalCompare, isExtinguisher, isoToLocalInputValue, localInputValueToIso } from "../shared/utils";
import { PhotoInput } from "../shared/PhotoInput";
import { SimpleModal } from "../shared/SimpleModal";
import { BackButton } from "../shared/BackButton";

async function saveInspectionBlobToFolder(insp, blob, efsm) {
  try {
    const year = insp.date ? new Date(insp.date).getFullYear() : new Date().getFullYear();
    const typeLabel = { annual: "Annual", bi_annual: "Bi-Annual", monthly: "Monthly" }[insp.inspection_type] || "Inspection";
    const folderName = `${typeLabel} ${year}`;
    const foldersRes = await axios.get(`${API}/site-folders/?site=${insp.site}`);
    let folder = foldersRes.data.find((f) => f.name === folderName);
    if (!folder) {
      // Race-safe get-or-create: SiteFolder enforces a unique (site, name)
      // pair, so when several inspections for the same site are completed
      // close together (exactly what Split Inspections encourages), more
      // than one of these can see "doesn't exist yet" before either
      // finishes creating it. If our create loses that race, the folder
      // now exists (made by whoever won) — re-fetch and use it instead of
      // dropping the whole upload.
      try {
        const cr = await axios.post(`${API}/site-folders/`, { site: insp.site, name: folderName, is_system: true });
        folder = cr.data;
      } catch (e) {
        const retryRes = await axios.get(`${API}/site-folders/?site=${insp.site}`);
        folder = retryRes.data.find((f) => f.name === folderName);
        if (!folder) throw e;
      }
    }
    const siteName = (insp.site_name || "site").replace(/[^a-z0-9]/gi, "_");
    // Capped so a long, descriptive tab name (e.g. "Upstairs Offices:
    // Extinguishers, Fire Hose Reels, Hydrants") can't push the full
    // filename past the server's stored-filename length limit.
    const sectionSlug = (insp.site_area_name || "WholeSite").replace(/[^a-z0-9]/gi, "_").slice(0, 60);
    const efsmSlug = efsm ? `_${efsm.replace(/[^a-z0-9]/gi, "_")}` : "";
    const fileName = `${siteName}_${sectionSlug}_${typeLabel}_${year}${efsmSlug}.pdf`;
    // Only replace THIS inspection's own prior save of the same variant
    // (full report vs. a specific category) — scoped by the real inspection
    // relation first, so a different inspection's record is never touched
    // even if it would otherwise produce a colliding name.
    const existingRes = await axios.get(`${API}/site-documents/?inspection=${insp.id}`);
    const existing = (existingRes.data || []).find((d) => d.name === fileName);
    if (existing) {
      if (existing.locked) {
        console.warn("Skipping auto-file: existing record is locked.");
        return true;
      }
      await axios.delete(`${API}/site-documents/${existing.id}/`);
    }
    const pdfFile = new File([blob], fileName, { type: "application/pdf" });
    const fd = new FormData();
    fd.append("file", pdfFile);
    fd.append("folder", folder.id);
    fd.append("inspection", insp.id);
    await axios.post(`${API}/site-documents/`, fd);
    return true;
  } catch (e) {
    console.warn("Auto-file inspection failed:", e);
    return false;
  }
}

// When the server received a photo. Deliberately not "taken at": the client
// downscales through a canvas before upload and that discards EXIF, so the
// camera's time is gone by then. This comes from the server clock.
function formatPhotoStamp(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleString("en-AU", {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}

// Shown when a tile's filter has nothing in it — distinct from an inspection
// with no assets at all, which is a different problem and keeps its own message.
const EMPTY_FILTER_MESSAGE = {
  // The goal state, and what a tech sees straight after Pass All.
  not_tested: "Nothing left to test.",
  needs_photo: "Every asset that needs a photo has one.",
  pass: "Nothing passed yet.",
  fail: "Nothing failed — good.",
  follow_ups: "No follow-ups on this inspection.",
};

// Mirrors InspectionItem.FOLLOWUP_FLAGS on the server, so a raised follow-up
// always reads as the button the tech actually pressed.
const FOLLOWUP_FLAGS = [
  ["followup_tag_missing", "Tag missing"],
  ["followup_signs_needed", "Signs needed"],
  ["followup_needs_hanging", "Needs hanging"],
];

export function InspectionSheet({ inspectionId, onBack }) {
  const [inspection, setInspection] = useState(null);
  const [items, setItems] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [error, setError] = useState("");
  const [uploadingPhoto, setUploadingPhoto] = useState(null);
  const [procedures, setProcedures] = useState([]);
  const [viewingProcedure, setViewingProcedure] = useState(null);
  const [failTypeMap, setFailTypeMap] = useState({});
  const [swapModal, setSwapModal] = useState(null); // inspection item being swapped
  const [swapDate, setSwapDate] = useState("");
  const [swapNotes, setSwapNotes] = useState("");
  const [swapSaving, setSwapSaving] = useState(false);
  const [swapError, setSwapError] = useState("");
  const [assetInfoModal, setAssetInfoModal] = useState(null); // item object | null
  const [assetInfoEdits, setAssetInfoEdits] = useState({}); // { assetId: { fieldId: value } }
  const [assetInfoPhotoEdits, setAssetInfoPhotoEdits] = useState({}); // { assetId: { slot: File } }
  const [assetInfoSaving, setAssetInfoSaving] = useState(null); // assetId being saved
  const [cylinderEditId, setCylinderEditId] = useState(null); // item id being edited
  const [cylinderDraft, setCylinderDraft] = useState("");
  const cylinderSaveTimerRef = useRef(null);
  const [locationEditId, setLocationEditId] = useState(null);
  const [locationDraft, setLocationDraft] = useState("");
  const locationSaveTimerRef = useRef(null);
  const [floorLevelEditId, setFloorLevelEditId] = useState(null);
  const [floorLevelDraft, setFloorLevelDraft] = useState("");
  const floorLevelSaveTimerRef = useRef(null);
  const [assetTypeEditId, setAssetTypeEditId] = useState(null);
  const [assetTypes, setAssetTypes] = useState([]);
  const [showAddAssetModal, setShowAddAssetModal] = useState(false);
  const [addAssetGroupKey, setAddAssetGroupKey] = useState("");
  const [addAssetTypeId, setAddAssetTypeId] = useState("");
  const [addAssetLocation, setAddAssetLocation] = useState("");
  const [addAssetFloorLevel, setAddAssetFloorLevel] = useState("");
  const [addAssetCylinderDate, setAddAssetCylinderDate] = useState("");
  const [addAssetItemNumber, setAddAssetItemNumber] = useState("");
  const [addAssetSaving, setAddAssetSaving] = useState(false);
  const [addAssetError, setAddAssetError] = useState("");
  const [editingInspectionDate, setEditingInspectionDate] = useState(false);
  const [inspectionDateDraft, setInspectionDateDraft] = useState("");
  const inspectionDateSaveTimerRef = useRef(null);
  const isMobile = useIsMobile();

  // Which summary tile is selected; drives the visible list.
  const [filter, setFilter] = useState("total");
  // Each item as it stood at the last regroup. The tile filter reads this
  // rather than live state, so marking a row Pass on the N/T list leaves it in
  // place, showing its new result, until Save — instead of vanishing under the
  // tech's thumb.
  const [regroupSnapshot, setRegroupSnapshot] = useState({});
  const itemsRef = useRef(items);
  itemsRef.current = items;
  const regroup = () => setRegroupSnapshot(itemsRef.current);
  const selectFilter = (key) => {
    setFilter(key);
    regroup();
  };
  // Which photo's remove button is armed, as "<itemId>:<slotIndex>". Removing
  // is irreversible and a thumbnail is a small target on a phone, so the first
  // tap only arms — same two-tap shape as the delete on the Bills rows.
  const [confirmPhoto, setConfirmPhoto] = useState(null);

  const loadInspection = () => {
    Promise.all([
      axios.get(`${API}/inspections/${inspectionId}/`),
      axios.get(`${API}/asset-fail-types/`),
    ])
      .then(([inspRes, ftRes]) => {
        const insp = inspRes.data;
        setInspection(insp);
        // Read-only here — written by the office on the site's Procedures tab.
        if (insp.site) {
          axios
            .get(`${API}/site-procedures/?site=${insp.site}`)
            .then((r) => setProcedures(r.data || []))
            .catch(() => setProcedures([]));
        }
        const itemMap = {};
        (insp.items || []).forEach((item) => {
          itemMap[item.id] = { ...item, dirty: false };
        });
        setItems(itemMap);
        // Reloads follow small edits like a location change; rows the tech has
        // already marked keep their place until Save. Only new items join.
        setRegroupSnapshot((prev) => ({ ...itemMap, ...prev }));
        // Land on what is left to do. On a fresh inspection everything is
        // untested, so this opens as the full list and shrinks from there.
        // Falling back to Total keeps a finished inspection from opening on an
        // empty screen.
        //
        // Resolved here, on load, and never again: recomputing per render would
        // throw the tech from N/T to Total the instant they marked the last
        // untested item.
        const anyUntested = Object.values(itemMap).some(
          (i) => i.result === "not_tested",
        );
        setFilter(anyUntested ? "not_tested" : "total");
        const ftMap = {};
        ftRes.data.forEach((ft) => {
          if (!ftMap[ft.category]) ftMap[ft.category] = [];
          ftMap[ft.category].push(ft);
        });
        setFailTypeMap(ftMap);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  };

  useEffect(() => {
    loadInspection();
  }, [inspectionId]);

  // Distinct category/unit groups already present in this inspection, each
  // carrying an "anchor" item id - quick-adding a missing asset is always
  // scoped to a category already represented here (see quick-add-asset on
  // the backend), never a brand new category. Declared above the loading/
  // not-found early returns below since it's a hook.
  const assetGroups = useMemo(() => {
    const map = {};
    Object.values(items).forEach((it) => {
      if (it.category_id == null) return;
      const key = `${it.category_id}::${it.unit_label || ""}`;
      if (!map[key]) {
        map[key] = {
          key,
          categoryId: it.category_id,
          categoryName: it.category_name,
          unitLabel: it.unit_label || null,
          anchorItemId: it.id,
        };
      }
    });
    return Object.values(map);
  }, [items]);

  useEffect(() => {
    axios.get(`${API}/asset-types/`).then((res) => setAssetTypes(res.data || [])).catch(() => {});
  }, []);

  const updateItem = (itemId, field, value) => {
    setItems((prev) => ({
      ...prev,
      [itemId]: { ...prev[itemId], [field]: value, dirty: true },
    }));
  };

  const saveItem = async (itemId) => {
    const item = items[itemId];
    if (!item.dirty) return;
    try {
      await axios.patch(`${API}/inspection-items/${itemId}/`, {
        result: item.result,
        notes: item.notes,
        fail_types: item.fail_types || [],
        swapped: item.swapped || false,
        new_cylinder_date: item.new_cylinder_date || null,
      });
      setItems((prev) => ({
        ...prev,
        [itemId]: { ...prev[itemId], dirty: false },
      }));
    } catch (e) {
      console.error("Failed to save item", itemId);
    }
  };

  const setResult = async (itemId, val) => {
    setItems((prev) => ({
      ...prev,
      [itemId]: { ...prev[itemId], result: val, dirty: true },
    }));
    try {
      await axios.patch(`${API}/inspection-items/${itemId}/`, { result: val });
      setItems((prev) => ({
        ...prev,
        [itemId]: { ...prev[itemId], dirty: false },
      }));
    } catch (e) {
      console.error("Failed to save result", itemId);
    }
  };

  const toggleFailType = async (itemId, failTypeId) => {
    const item = items[itemId];
    const current = item.fail_types || [];
    const next = current.includes(failTypeId)
      ? current.filter((id) => id !== failTypeId)
      : [...current, failTypeId];
    setItems((prev) => ({
      ...prev,
      [itemId]: { ...prev[itemId], fail_types: next, dirty: true },
    }));
    try {
      await axios.patch(`${API}/inspection-items/${itemId}/`, { fail_types: next });
      setItems((prev) => ({
        ...prev,
        [itemId]: { ...prev[itemId], dirty: false },
      }));
    } catch (e) {
      console.error("Failed to save fail types", itemId);
    }
  };

  // Follow-up flags. Independent of `result` and of each other, so each is its
  // own boolean patch rather than anything that touches the pass/fail state.
  const toggleFollowUp = async (itemId, field) => {
    const next = !items[itemId][field];
    setItems((prev) => ({
      ...prev,
      [itemId]: { ...prev[itemId], [field]: next, dirty: true },
    }));
    try {
      await axios.patch(`${API}/inspection-items/${itemId}/`, { [field]: next });
      setItems((prev) => ({
        ...prev,
        [itemId]: { ...prev[itemId], dirty: false },
      }));
    } catch (e) {
      console.error("Failed to save follow-up flag", itemId);
    }
  };

  const silentFileInspectionPDF = async () => {
    try {
      const inspRes = await axios.get(`${API}/inspections/${inspection.id}/`);
      const fresh = inspRes.data;
      // Non-fatal: an unsigned report is far better than no report. This
      // request used to be able to 403 and take the entire auto-file with it,
      // which loadTechSignatures preserves by never throwing.
      const signaturesMap = await loadTechSignatures();
      const blob = await generateInspectionPDF(fresh, signaturesMap, { noDownload: true });
      const filed = await saveInspectionBlobToFolder(fresh, blob, null);
      if (!filed) {
        setError("Your work is saved, but the Records copy of the report didn't file. It will retry on the next save.");
      } else {
        setError((prev) =>
          prev === "Your work is saved, but the Records copy of the report didn't file. It will retry on the next save." ? "" : prev
        );
      }
    } catch (e) {
      console.warn("Auto-file inspection failed:", e);
      setError("Your work is saved, but the Records copy of the report didn't file. It will retry on the next save.");
    }
  };

  const saveAll = async () => {
    setSaving(true);
    const dirtyItems = Object.values(items).filter((i) => i.dirty);
    for (const item of dirtyItems) {
      await saveItem(item.id);
    }
    setSaving(false);
    regroup();
    silentFileInspectionPDF();
  };

  const passAll = async () => {
    const allItemsList = Object.values(items);
    const toPass = allItemsList.filter((i) => i.result !== "pass");
    if (toPass.length === 0) return;
    setItems((prev) => {
      const updated = { ...prev };
      toPass.forEach((i) => {
        updated[i.id] = { ...updated[i.id], result: "pass", dirty: true };
      });
      return updated;
    });
    for (const item of toPass) {
      try {
        await axios.patch(`${API}/inspection-items/${item.id}/`, {
          result: "pass",
        });
      } catch (e) {
        console.error("passAll save failed", item.id);
      }
    }
    setItems((prev) => {
      const updated = { ...prev };
      toPass.forEach((i) => {
        if (updated[i.id]) updated[i.id] = { ...updated[i.id], dirty: false };
      });
      return updated;
    });
    // The pass results rendered during the PATCH loop, so the ref has them.
    regroup();
  };

  const passRemaining = async () => {
    const allItemsList = Object.values(items);
    const toPass = allItemsList.filter((i) => i.result === "not_tested");
    if (toPass.length === 0) return;
    setItems((prev) => {
      const updated = { ...prev };
      toPass.forEach((i) => {
        updated[i.id] = { ...updated[i.id], result: "pass", dirty: true };
      });
      return updated;
    });
    for (const item of toPass) {
      try {
        await axios.patch(`${API}/inspection-items/${item.id}/`, {
          result: "pass",
        });
      } catch (e) {
        console.error("passRemaining save failed", item.id);
      }
    }
    setItems((prev) => {
      const updated = { ...prev };
      toPass.forEach((i) => {
        if (updated[i.id]) updated[i.id] = { ...updated[i.id], dirty: false };
      });
      return updated;
    });
    // The pass results rendered during the PATCH loop, so the ref has them.
    regroup();
  };

  const PHOTO_FIELDS = ["photo_1", "photo_2", "photo_3"];

  // Takes a list, because the picker allows selecting several library photos
  // at once. Uploads run SEQUENTIALLY and track the next free slot locally —
  // deriving it from component state (which only refreshes once a request
  // resolves) would make every file in one batch target photo_1.
  // Remove one photo. Clears that slot and leaves the gap: shuffling the others
  // down would mean re-uploading files just to move them, and uploadPhotos now
  // fills the first free slot anyway.
  //
  // The stored file itself is left on disk. Detaching it is what the tech asked
  // for; deleting the bytes is not, and is not undoable.
  const removePhoto = async (itemId, slotIndex) => {
    const field = PHOTO_FIELDS[slotIndex];
    try {
      const res = await axios.patch(`${API}/inspection-items/${itemId}/`, {
        [field]: null,
      });
      setItems((prev) => ({
        ...prev,
        [itemId]: {
          ...prev[itemId],
          photo_1_url: res.data.photo_1_url,
          photo_2_url: res.data.photo_2_url,
          photo_3_url: res.data.photo_3_url,
          photo_1_at: res.data.photo_1_at,
          photo_2_at: res.data.photo_2_at,
          photo_3_at: res.data.photo_3_at,
        },
      }));
    } catch (e) {
      console.error("Failed to remove photo", itemId, field);
    }
  };

  const uploadPhotos = async (itemId, fileList) => {
    const files = Array.from(fileList || []);
    if (files.length === 0) return;
    setUploadingPhoto(itemId);
    try {
      const item = items[itemId];
      // Track which slots are actually free, rather than counting how many are
      // taken. Removing a photo leaves a gap — delete slot 2 and slots 1 and 3
      // are filled — so counting would point the next upload at photo_3 and
      // quietly overwrite it.
      const urls = [item.photo_1_url, item.photo_2_url, item.photo_3_url];
      const freeSlots = PHOTO_FIELDS.map((_, idx) => idx).filter(
        (idx) => !urls[idx],
      );
      for (const file of files) {
        const nextSlot = freeSlots.shift();
        if (nextSlot === undefined) break;
        // Already downscaled and re-encoded by PhotoInput.
        const formData = new FormData();
        formData.append(PHOTO_FIELDS[nextSlot], file);
        const res = await axios.patch(
          `${API}/inspection-items/${itemId}/`,
          formData,
          { headers: { "Content-Type": "multipart/form-data" } },
        );
        setItems((prev) => ({
          ...prev,
          [itemId]: {
            ...prev[itemId],
            photo_1_url: res.data.photo_1_url,
            photo_2_url: res.data.photo_2_url,
            photo_3_url: res.data.photo_3_url,
            photo_1_at: res.data.photo_1_at,
            photo_2_at: res.data.photo_2_at,
            photo_3_at: res.data.photo_3_at,
          },
        }));
      }
    } catch (e) {
      console.error("Photo upload failed", e);
    }
    setUploadingPhoto(null);
  };

  const completeInspection = async () => {
    const failsWithoutNotes = Object.values(items).filter(
      (i) => i.result === "fail" && !i.notes.trim(),
    );
    // Photo evidence. A category can require one on every asset whatever the
    // result — an exit that passed with no picture proves nothing — so this
    // refuses in the same shape as a failure without notes.
    const needPhotos = Object.values(items).filter(missingEvidence);
    if (needPhotos.length > 0) {
      // Show them rather than leaving the tech to hunt: the summary tiles
      // already drive the list, so point it at exactly these.
      selectFilter("needs_photo");
      setError(
        `${needPhotos.length} asset(s) need a photo before this inspection ` +
          `can be completed. The list is showing them.`,
      );
      return;
    }
    if (failsWithoutNotes.length > 0) {
      setError(
        `${failsWithoutNotes.length} failed item(s) are missing notes. Please add notes for all failures.`,
      );
      return;
    }
    setCompleting(true);
    setError("");
    await saveAll();
    const allItems = Object.values(items);
    const hasFail = allItems.some((i) => i.result === "fail" && !i.swapped);
    const finalStatus = hasFail ? "failed" : "passed";
    try {
      await axios.patch(`${API}/inspections/${inspectionId}/`, {
        status: finalStatus,
      });
      onBack();
    } catch (e) {
      setError("Something went wrong completing the inspection.");
    }
    setCompleting(false);
  };

  if (loading) return <div style={styles.loading}>Loading inspection...</div>;
  if (!inspection)
    return <div style={styles.loading}>Inspection not found.</div>;

  if (inspection.locked) {
    return (
      <div>
        <div style={styles.topbar}>
          <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
            <BackButton onClick={onBack} />
            <div style={styles.pageTitle}>{inspection.site_name}</div>
          </div>
        </div>
        <div style={styles.content}>
          <div style={{ ...styles.card, padding: "40px", textAlign: "center" }}>
            <LuLock size={32} style={{ color: colors.amber, marginBottom: "12px" }} />
            <div style={{ fontSize: "16px", fontWeight: "600", color: colors.text, marginBottom: "8px" }}>
              This inspection's report has been locked
            </div>
            <div style={{ fontSize: "14px", color: colors.slate }}>
              Admin or management has locked this inspection's record in Records.
              It can't be reopened or edited until they unlock it.
            </div>
          </div>
        </div>
      </div>
    );
  }

  const isCompleted = inspection.status !== "in_progress";
  const typeLabel =
    { monthly: "Monthly", bi_annual: "Bi-Annual", annual: "Annual" }[
      inspection.inspection_type
    ] || inspection.inspection_type;
  const formatDate = (d) =>
    d
      ? new Date(d).toLocaleDateString("en-AU", {
          day: "2-digit",
          month: "short",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        })
      : "—";

  // One flat, ungrouped list — matching the Asset Register's own tabs, which
  // never sub-group by category (Category is a column there, not a header),
  // sorted by unit first (when combining multiple tenants into one report,
  // so each unit's items stay together) then item_number. An inspection is
  // already scoped to one tab, so it should render exactly like that tab
  // does, even when the tab combines multiple EFSM categories together.
  const sortedItems = Object.values(items).sort((a, b) => {
    const au = a.unit_order ?? -1;
    const bu = b.unit_order ?? -1;
    if (au !== bu) return au - bu;
    // Matches SiteUnit's own natural-sort ordering convention (order,
    // then unit_number) so tenant order here is consistent with every
    // other unit listing in the app.
    const numCmp = naturalCompare(a.unit_number, b.unit_number);
    if (numCmp !== 0) return numCmp;
    if (a.item_number == null) return 1;
    if (b.item_number == null) return -1;
    return a.item_number - b.item_number;
  });
  // Some categories need a photo on every asset whatever the result: a passed
  // exit with no picture proves nothing, and "can someone get out?" is the
  // whole question a paths-of-travel inspection answers. Driven by a flag on
  // the category rather than its name, so a rename cannot switch it off.
  const needsEvidence = (item) => !!item.category_requires_photo_evidence;
  const missingEvidence = (item) =>
    needsEvidence(item) &&
    !item.photo_1_url &&
    !item.photo_2_url &&
    !item.photo_3_url;

  // One predicate per summary tile, driving BOTH its count and the list filter
  // below. Defining them twice is how a tile ends up claiming three items and
  // showing four.
  //
  // The groups overlap on purpose: an untested extinguisher already flagged for
  // a missing tag is both still to do and already carrying a loose end, so it
  // appears under N/T and Follow Ups. Only "total" is guaranteed to hold
  // everything — these counts are not meant to sum to it.
  const TILE_FILTERS = {
    total: () => true,
    pass: (i) => i.result === "pass" || i.swapped,
    fail: (i) => i.result === "fail" && !i.swapped,
    not_tested: (i) => i.result === "not_tested",
    // Everything this inspection will leave outstanding. Not Found belongs here
    // rather than in a group of its own: a missing asset raises a follow-up
    // line through the same path as a flagged extinguisher, and the Follow Ups
    // tab lists the two together.
    //
    // Counts items, not reasons: one extinguisher with three things wrong is
    // one thing for the tech to look at.
    follow_ups: (i) =>
      i.result === "not_found" || FOLLOWUP_FLAGS.some(([field]) => i[field]),
    // Not a tile — reached only when completing is blocked, to show the tech
    // exactly what is holding it up.
    needs_photo: missingEvidence,
  };
  // What the selected tile shows. Rows render from live state — only which
  // ones are present is decided from the snapshot, so a row moves groups on
  // Save, a tile change or Pass All/Rest, not on every tap.
  const tileFilter = TILE_FILTERS[filter] || TILE_FILTERS.total;
  const visibleItems = sortedItems.filter((i) =>
    tileFilter(regroupSnapshot[i.id] || i),
  );

  const hasUnits = sortedItems.some((i) => i.unit_label);
  // Cylinder dates only mean anything for pressurised extinguishers. Keyed on
  // the asset TYPE, not the category, because the extinguisher register also
  // holds fire blankets, which have no cylinder.
  const showCylinder = sortedItems.some((i) => isExtinguisher(i.asset_type_name));
  // One definition shared by the header and every row — they used to carry
  // duplicate literals, which is how a column and its header drift apart.
  const gridCols = [
    "50px",
    hasUnits && "1.2fr",
    hasUnits ? "1.3fr" : "1.6fr",
    "1.1fr",
    "1.6fr",
    showCylinder && "1.2fr",
    "230px",
  ]
    .filter(Boolean)
    .join(" ");

  const openAddAssetModal = () => {
    const group = assetGroups[0];
    const anchor = group ? items[group.anchorItemId] : null;
    setAddAssetGroupKey(group ? group.key : "");
    setAddAssetTypeId(anchor?.asset_type_id || "");
    setAddAssetLocation("");
    setAddAssetFloorLevel(anchor?.floor_level || "");
    setAddAssetCylinderDate("");
    setAddAssetItemNumber("");
    setAddAssetError("");
    setShowAddAssetModal(true);
  };

  const handleAddAssetGroupChange = (key) => {
    setAddAssetGroupKey(key);
    const group = assetGroups.find((g) => g.key === key);
    const anchor = group ? items[group.anchorItemId] : null;
    setAddAssetTypeId(anchor?.asset_type_id || "");
    setAddAssetFloorLevel(anchor?.floor_level || "");
  };

  const handleAddAsset = async () => {
    const group = assetGroups.find((g) => g.key === addAssetGroupKey);
    if (!group) { setAddAssetError("Pick a category."); return; }
    if (!addAssetLocation.trim()) { setAddAssetError("Location is required."); return; }
    setAddAssetSaving(true);
    setAddAssetError("");
    try {
      const res = await axios.post(`${API}/inspection-items/quick-add-asset/`, {
        inspection: inspectionId,
        based_on_item: group.anchorItemId,
        asset_type: addAssetTypeId || null,
        location: addAssetLocation.trim(),
        floor_level: addAssetFloorLevel,
        item_number: addAssetItemNumber ? Number(addAssetItemNumber) : null,
        cylinder_date: addAssetCylinderDate || null,
      });
      setItems((prev) => ({ ...prev, [res.data.id]: { ...res.data, dirty: false } }));
      setShowAddAssetModal(false);
    } catch (err) {
      setAddAssetError(err.response?.data?.detail || "Failed to add asset.");
    }
    setAddAssetSaving(false);
  };

  const allItems = Object.values(items);
  const countFor = (key) => allItems.filter(TILE_FILTERS[key]).length;

  const passCount = countFor("pass");
  const failCount = countFor("fail");
  const notTestedCount = countFor("not_tested");
  const followUpCount = countFor("follow_ups");

  const resultButtons = (item) =>
    [
      ["pass", "Pass", colors.green, "rgba(22,163,74,0.35)"],
      ["fail", "Fail", colors.red, "rgba(230,57,70,0.35)"],
      // Not Found lives in the grey row below with the follow-ups, not here.
      // It is still a result — picking it clears Pass or Fail — but it belongs
      // visually with the other "neither good news nor bad" outcomes.
    ].map(([val, label, color, glow]) => {
      const active = item.result === val;
      return (
        <button
          key={val}
          disabled={isCompleted}
          onClick={() => setResult(item.id, val)}
          style={{
            flex: 1,
            padding: isMobile ? "12px 8px" : "7px 12px",
            borderRadius: "20px",
            border: `2px solid ${active ? color : colors.border}`,
            backgroundColor: active ? color : "white",
            color: active ? "white" : "#64748b",
            fontWeight: "600",
            fontSize: isMobile ? "15px" : "12px",
            cursor: isCompleted ? "default" : "pointer",
            transition: "all 0.15s",
            minHeight: "auto",
            boxShadow: active ? `0 0 0 3px ${glow}` : "none",
          }}
        >
          {label}
        </button>
      );
    });

  // The grey row: everything that is neither a pass nor a fail.
  //
  //   Not Found     — the asset was not there to test. Still a RESULT, so it
  //                   clears Pass or Fail; it just belongs here visually rather
  //                   than beside them. Shown for every asset.
  //   Follow-ups    — minor things that leave an inspection unfinished without
  //                   being defects: no tag, no signage, sitting on the floor.
  //                   Independent toggles that sit alongside any result.
  //                   Extinguishers only, via the same isExtinguisher() the
  //                   cylinder-date row uses.
  //
  // Neither kind takes photos on the strength of these buttons alone, and
  // nothing here reaches the customer's report — completing the inspection
  // raises a site follow-up so the office can chase them.
  const greyButtons = (item) => {
    const buttons = [
      {
        key: "not_found",
        label: "Not Found",
        active: item.result === "not_found",
        onClick: () => setResult(item.id, "not_found"),
        title: "The asset was not there to test",
      },
      ...(isExtinguisher(item.asset_type_name)
        ? FOLLOWUP_FLAGS.map(([field, label]) => ({
            key: field,
            label,
            active: !!item[field],
            onClick: () => toggleFollowUp(item.id, field),
            title: "Follow-up — not a pass or a fail",
          }))
        : []),
    ];
    return (
      <div style={{ display: "flex", flexWrap: "wrap", gap: "6px", marginTop: "6px" }}>
        {buttons.map(({ key, label, active, onClick, title }) => {
          return (
            <button
              key={key}
              disabled={isCompleted}
              onClick={onClick}
              title={title}
              style={{
                padding: isMobile ? "10px 12px" : "5px 10px",
                borderRadius: "20px",
                border: `2px solid ${active ? colors.slate : colors.border}`,
                backgroundColor: active ? colors.slate : "white",
                color: active ? "white" : "#64748b",
                fontWeight: "600",
                fontSize: isMobile ? "13px" : "11px",
                cursor: isCompleted ? "default" : "pointer",
                transition: "all 0.15s",
                boxShadow: active ? "0 0 0 3px rgba(100,116,139,0.35)" : "none",
              }}
            >
              {label}
            </button>
          );
        })}
      </div>
    );
  };

  const failTypeChecklist = (item) => {
    if (item.result !== "fail") return null;
    const options = failTypeMap[item.category_id] || [];
    if (options.length === 0) return null;
    const selected = item.fail_types || [];
    return (
      <div style={{ marginBottom: "10px" }}>
        <div style={{ fontSize: "12px", fontWeight: "600", color: "#666", textTransform: "uppercase", marginBottom: "6px" }}>
          Fail Reasons
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
          {options.map((ft) => {
            const active = selected.includes(ft.id);
            return (
              <button
                key={ft.id}
                disabled={isCompleted}
                onClick={() => !isCompleted && toggleFailType(item.id, ft.id)}
                style={{
                  padding: "4px 12px",
                  borderRadius: "20px",
                  border: `1.5px solid ${active ? colors.red : colors.border}`,
                  backgroundColor: active ? "#fee2e2" : "white",
                  color: active ? "#b91c1c" : "#64748b",
                  fontSize: "12px",
                  fontWeight: active ? "700" : "500",
                  cursor: isCompleted ? "default" : "pointer",
                  minHeight: "auto",
                  transition: "all 0.12s",
                }}
              >
                {active ? "✓ " : ""}{ft.name}
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  const handleSwapAndGo = async () => {
    if (!swapDate) { setSwapError("New cylinder date is required."); return; }
    setSwapError("");
    setSwapSaving(true);
    const item = swapModal;
    const noteText = swapNotes.trim() || "Cylinder 5 yearly swap over done during inspection.";
    try {
      await axios.patch(`${API}/inspection-items/${item.id}/`, {
        swapped: true,
        new_cylinder_date: swapDate,
        notes: noteText,
        result: "fail",
        fail_types: item.fail_types || [],
      });
      setSwapModal(null);
      setSwapDate("");
      setSwapNotes("");
      setSwapError("");
      loadInspection();
    } catch (e) {
      setSwapError("Something went wrong. Please try again.");
    }
    setSwapSaving(false);
  };

  const swapAndGoSection = (item) => {
    if (!item.allow_swap_and_go || item.result !== "fail") return null;
    if (item.swapped) {
      return (
        <div style={{
          display: "flex", alignItems: "center", gap: "8px",
          backgroundColor: "#f0fdf4", border: "1.5px solid #86efac",
          borderRadius: "8px", padding: "8px 12px", marginBottom: "10px",
        }}>
          <TbFireExtinguisher size={16} color="#16a34a" />
          <span style={{ fontSize: "13px", fontWeight: "700", color: "#16a34a" }}>
            Replaced on Site
          </span>
        </div>
      );
    }
    return (
      <button
        disabled={isCompleted}
        onClick={() => {
          setSwapModal(item);
          setSwapDate("");
          setSwapNotes("");
        }}
        style={{
          display: "flex", alignItems: "center", gap: "6px",
          backgroundColor: "transparent", color: "#16a34a",
          border: "2px solid #16a34a", padding: "6px 14px",
          borderRadius: "20px", fontSize: "13px", fontWeight: "700",
          cursor: isCompleted ? "default" : "pointer", minHeight: "auto",
          marginBottom: "10px", transition: "all 0.12s",
        }}
      >
        <TbFireExtinguisher size={15} />
        Swap &amp; Go
      </button>
    );
  };

  const photoSection = (item) =>
    (item.result === "fail" || needsEvidence(item)) && (
      <div
        style={{
          marginTop: "12px",
          paddingTop: "12px",
          borderTop: `1px dashed ${colors.border}`,
        }}
      >
        <div
          style={{
            fontSize: "12px",
            fontWeight: "600",
            color: "#666",
            textTransform: "uppercase",
            marginBottom: "8px",
          }}
        >
          📷{" "}
          {needsEvidence(item)
            ? `Evidence Photo — required${
                missingEvidence(item) ? " (none yet)" : ""
              }`
            : "Fail Photos (optional, max 3)"}
        </div>
        <div
          style={{
            display: "flex",
            gap: "10px",
            flexWrap: "wrap",
            alignItems: "flex-start",
          }}
        >
          {[
            { url: item.photo_1_url, at: item.photo_1_at },
            { url: item.photo_2_url, at: item.photo_2_at },
            { url: item.photo_3_url, at: item.photo_3_at },
          ]
            // Keep the real slot index — it is what removePhoto patches, and a
            // gap left by an earlier removal must not renumber the rest.
            .map((p, slotIndex) => ({ ...p, slotIndex }))
            .filter(({ url }) => Boolean(url))
            .map(({ url, at, slotIndex }, pi) => {
              const armed = confirmPhoto === `${item.id}:${slotIndex}`;
              return (
                <div key={slotIndex} style={{ position: "relative" }}>
                  <img
                    src={url}
                    alt={"Photo " + (pi + 1)}
                    style={{
                      width: isMobile ? "100px" : "80px",
                      height: isMobile ? "100px" : "80px",
                      objectFit: "cover",
                      borderRadius: "6px",
                      border: `1px solid ${colors.border}`,
                      display: "block",
                    }}
                  />
                  {!isCompleted && (
                    <button
                      onClick={() => {
                        if (!armed) {
                          setConfirmPhoto(`${item.id}:${slotIndex}`);
                          return;
                        }
                        setConfirmPhoto(null);
                        removePhoto(item.id, slotIndex);
                      }}
                      onBlur={() => armed && setConfirmPhoto(null)}
                      title={armed ? "Tap again to remove" : "Remove this photo"}
                      style={{
                        position: "absolute",
                        top: "-6px",
                        right: "-6px",
                        // Grey, not red: removing a photo you just took is
                        // routine, not an error or a hazard.
                        width: armed ? "auto" : "22px",
                        height: "22px",
                        padding: armed ? "0 8px" : 0,
                        borderRadius: "11px",
                        border: `1px solid ${colors.border}`,
                        backgroundColor: armed ? colors.slate : colors.white,
                        color: armed ? colors.white : colors.slate,
                        fontSize: armed ? "10px" : "14px",
                        fontWeight: "600",
                        lineHeight: 1,
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
                      }}
                    >
                      {armed ? "Remove?" : "×"}
                    </button>
                  )}
                  <div
                    style={{
                      fontSize: "10px",
                      color: "#999",
                      textAlign: "center",
                      marginTop: "2px",
                    }}
                  >
                    {at ? formatPhotoStamp(at) : `Photo ${pi + 1}`}
                  </div>
                </div>
              );
            })}
          {!isCompleted &&
            [item.photo_1_url, item.photo_2_url, item.photo_3_url].filter(
              Boolean,
            ).length < 3 && (
              <PhotoInput
                compact
                multiple
                busy={uploadingPhoto === item.id}
                onFiles={(files) => uploadPhotos(item.id, files)}
              />
            )}
          {uploadingPhoto === item.id && (
            <div
              style={{
                fontSize: "12px",
                color: colors.blue,
                alignSelf: "center",
              }}
            >
              Uploading...
            </div>
          )}
        </div>
      </div>
    );

  const saveAssetInfo = async (item) => {
    const assetId = item.asset;
    setAssetInfoSaving(assetId);
    const edits = assetInfoEdits[assetId] || {};
    const existingValues = item.asset_info || [];

    const valuePromises = Object.entries(edits).map(([fieldId, value]) => {
      const existing = existingValues.find((v) => v.field_id === parseInt(fieldId));
      if (existing) {
        return axios.patch(`${API}/asset-info-values/${existing.id}/`, { value });
      }
      return axios.post(`${API}/asset-info-values/`, {
        asset: assetId,
        field: parseInt(fieldId),
        value,
      });
    });

    const photoEdits = assetInfoPhotoEdits[assetId] || {};
    const photoPromises = Object.entries(photoEdits).map(async ([slot, file]) => {
      const formData = new FormData();
      formData.append(`info_photo_${slot}`, file);
      return axios.patch(`${API}/assets/${assetId}/`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
    });

    try {
      await Promise.all([...valuePromises, ...photoPromises]);
      setAssetInfoEdits((prev) => { const n = { ...prev }; delete n[assetId]; return n; });
      setAssetInfoPhotoEdits((prev) => { const n = { ...prev }; delete n[assetId]; return n; });
      setAssetInfoModal(null);
      loadInspection();
    } catch {}
    setAssetInfoSaving(null);
  };

  const formatCylinderDate = (d) =>
    d
      ? new Date(d + "T00:00:00").toLocaleDateString("en-AU", {
          month: "short",
          year: "numeric",
        })
      : null;

  const isCylinderOverdue = (d) => {
    if (!d) return false;
    const fiveYearsAgo = new Date();
    fiveYearsAgo.setFullYear(fiveYearsAgo.getFullYear() - 5);
    return new Date(d + "T00:00:00") < fiveYearsAgo;
  };

  const persistCylinderDate = async (item, newDate) => {
    const trimmed = newDate || null;
    if (trimmed === (item.cylinder_date || null)) return;
    try {
      await axios.patch(`${API}/inspection-items/${item.id}/`, {
        updated_cylinder_date: trimmed,
      });
      loadInspection();
    } catch (e) {
      window.alert("Failed to update cylinder date.");
    }
  };

  const flushCylinderSave = (item) => {
    if (cylinderSaveTimerRef.current) {
      clearTimeout(cylinderSaveTimerRef.current);
      cylinderSaveTimerRef.current = null;
      persistCylinderDate(item, cylinderDraft);
    }
  };

  const cylinderDateSection = (item) => {
    if (!isExtinguisher(item.asset_type_name)) return null;
    if (cylinderEditId === item.id) {
      return (
        <input
          type="date"
          autoFocus
          disabled={isCompleted}
          value={cylinderDraft}
          onClick={(e) => e.stopPropagation()}
          onChange={(e) => {
            const val = e.target.value;
            setCylinderDraft(val);
            if (cylinderSaveTimerRef.current) clearTimeout(cylinderSaveTimerRef.current);
            cylinderSaveTimerRef.current = setTimeout(() => {
              cylinderSaveTimerRef.current = null;
              persistCylinderDate(item, val);
            }, 700);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === "Escape") e.target.blur();
          }}
          onBlur={() => {
            flushCylinderSave(item);
            setCylinderEditId(null);
          }}
          style={{
            fontSize: "13px", padding: "2px 4px", borderRadius: "4px",
            border: `1px solid ${colors.border}`,
          }}
        />
      );
    }
    const overdue = isCylinderOverdue(item.cylinder_date);
    return (
      <span
        onClick={(e) => {
          e.stopPropagation();
          if (isCompleted) return;
          setCylinderDraft(item.cylinder_date || "");
          setCylinderEditId(item.id);
        }}
        style={{
          cursor: isCompleted ? "default" : "pointer",
          color: overdue ? colors.red : item.cylinder_date ? colors.text : "#999",
          fontWeight: overdue ? "700" : "400",
          fontSize: "13px",
        }}
      >
        {item.cylinder_date ? formatCylinderDate(item.cylinder_date) : "—"}
      </span>
    );
  };

  const persistLocation = async (item, newVal) => {
    if (newVal === (item.location || "")) return;
    try {
      await axios.patch(`${API}/inspection-items/${item.id}/`, { updated_location: newVal });
      loadInspection();
    } catch (e) {
      window.alert("Failed to update location.");
    }
  };

  const flushLocationSave = (item) => {
    if (locationSaveTimerRef.current) {
      clearTimeout(locationSaveTimerRef.current);
      locationSaveTimerRef.current = null;
      persistLocation(item, locationDraft);
    }
  };

  const locationSection = (item) => {
    if (locationEditId === item.id) {
      return (
        <input
          type="text"
          autoFocus
          disabled={isCompleted}
          value={locationDraft}
          onClick={(e) => e.stopPropagation()}
          onChange={(e) => {
            const val = e.target.value;
            setLocationDraft(val);
            if (locationSaveTimerRef.current) clearTimeout(locationSaveTimerRef.current);
            locationSaveTimerRef.current = setTimeout(() => {
              locationSaveTimerRef.current = null;
              persistLocation(item, val);
            }, 700);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === "Escape") e.target.blur();
          }}
          onBlur={() => {
            flushLocationSave(item);
            setLocationEditId(null);
          }}
          style={{
            fontSize: "13px", padding: "2px 4px", borderRadius: "4px",
            border: `1px solid ${colors.border}`, width: "100%",
          }}
        />
      );
    }
    return (
      <span
        onClick={(e) => {
          e.stopPropagation();
          if (isCompleted) return;
          setLocationDraft(item.location || "");
          setLocationEditId(item.id);
        }}
        style={{
          cursor: isCompleted ? "default" : "pointer",
          color: item.location ? colors.text : "#999",
        }}
      >
        {item.location || "No location"}
      </span>
    );
  };

  const persistFloorLevel = async (item, newVal) => {
    if (newVal === (item.floor_level || "")) return;
    try {
      await axios.patch(`${API}/inspection-items/${item.id}/`, { updated_floor_level: newVal });
      loadInspection();
    } catch (e) {
      window.alert("Failed to update floor level.");
    }
  };

  const flushFloorLevelSave = (item) => {
    if (floorLevelSaveTimerRef.current) {
      clearTimeout(floorLevelSaveTimerRef.current);
      floorLevelSaveTimerRef.current = null;
      persistFloorLevel(item, floorLevelDraft);
    }
  };

  const floorLevelSection = (item) => {
    if (floorLevelEditId === item.id) {
      return (
        <input
          type="text"
          autoFocus
          disabled={isCompleted}
          value={floorLevelDraft}
          onClick={(e) => e.stopPropagation()}
          onChange={(e) => {
            const val = e.target.value;
            setFloorLevelDraft(val);
            if (floorLevelSaveTimerRef.current) clearTimeout(floorLevelSaveTimerRef.current);
            floorLevelSaveTimerRef.current = setTimeout(() => {
              floorLevelSaveTimerRef.current = null;
              persistFloorLevel(item, val);
            }, 700);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === "Escape") e.target.blur();
          }}
          onBlur={() => {
            flushFloorLevelSave(item);
            setFloorLevelEditId(null);
          }}
          style={{
            fontSize: "13px", padding: "2px 4px", borderRadius: "4px",
            border: `1px solid ${colors.border}`, width: "100%",
          }}
        />
      );
    }
    return (
      <span
        onClick={(e) => {
          e.stopPropagation();
          if (isCompleted) return;
          setFloorLevelDraft(item.floor_level || "");
          setFloorLevelEditId(item.id);
        }}
        style={{
          cursor: isCompleted ? "default" : "pointer",
          color: item.floor_level ? colors.text : "#999",
        }}
      >
        {item.floor_level || "—"}
      </span>
    );
  };

  const persistAssetType = async (item, newTypeId) => {
    try {
      await axios.patch(`${API}/inspection-items/${item.id}/`, { updated_asset_type: newTypeId });
      loadInspection();
    } catch (e) {
      window.alert(
        e.response?.data?.updated_asset_type?.[0] || "Failed to update asset type."
      );
    }
    setAssetTypeEditId(null);
  };

  const assetTypeSection = (item) => {
    if (assetTypeEditId === item.id) {
      const options = assetTypes.filter((t) => t.category === item.category_id && t.is_active !== false);
      return (
        <select
          autoFocus
          disabled={isCompleted}
          value={item.asset_type_id || ""}
          onClick={(e) => e.stopPropagation()}
          onChange={(e) => persistAssetType(item, e.target.value ? Number(e.target.value) : null)}
          onBlur={() => setAssetTypeEditId(null)}
          style={{
            fontSize: "13px", padding: "2px 4px", borderRadius: "4px",
            border: `1px solid ${colors.border}`,
          }}
        >
          <option value="">—</option>
          {options.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
        </select>
      );
    }
    return (
      <span
        onClick={(e) => {
          e.stopPropagation();
          if (isCompleted) return;
          setAssetTypeEditId(item.id);
        }}
        style={{ cursor: isCompleted ? "default" : "pointer", color: colors.text }}
      >
        {item.asset_type_name || "—"}
      </span>
    );
  };

  const persistInspectionDate = async (newDate) => {
    if (!newDate || newDate === isoToLocalInputValue(inspection.date)) return;
    try {
      await axios.patch(`${API}/inspections/${inspectionId}/`, { date: localInputValueToIso(newDate) });
      loadInspection();
    } catch (e) {
      window.alert("Failed to update the inspection date.");
    }
  };

  const flushInspectionDateSave = () => {
    if (inspectionDateSaveTimerRef.current) {
      clearTimeout(inspectionDateSaveTimerRef.current);
      inspectionDateSaveTimerRef.current = null;
      persistInspectionDate(inspectionDateDraft);
    }
  };

  const inspectionDateSection = () => {
    if (editingInspectionDate) {
      return (
        <input
          type="datetime-local"
          autoFocus
          disabled={isCompleted}
          value={inspectionDateDraft}
          onClick={(e) => e.stopPropagation()}
          onChange={(e) => {
            const val = e.target.value;
            setInspectionDateDraft(val);
            if (inspectionDateSaveTimerRef.current) clearTimeout(inspectionDateSaveTimerRef.current);
            inspectionDateSaveTimerRef.current = setTimeout(() => {
              inspectionDateSaveTimerRef.current = null;
              persistInspectionDate(val);
            }, 700);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === "Escape") e.target.blur();
          }}
          onBlur={() => {
            flushInspectionDateSave();
            setEditingInspectionDate(false);
          }}
          style={{
            fontSize: "13px", padding: "2px 4px", borderRadius: "4px",
            border: `1px solid ${colors.border}`,
          }}
        />
      );
    }
    return (
      <span
        onClick={(e) => {
          e.stopPropagation();
          if (isCompleted) return;
          setInspectionDateDraft(isoToLocalInputValue(inspection.date));
          setEditingInspectionDate(true);
        }}
        style={{ cursor: isCompleted ? "default" : "pointer", textDecoration: isCompleted ? "none" : "underline dotted" }}
      >
        {formatDate(inspection.date)}
      </span>
    );
  };

  const assetInfoButton = (item) => {
    if (!item.allow_asset_info) return null;
    const hasSavedInfo = item.asset_info?.some((v) => v.value) || item.asset_info_photos?.length;
    return (
      <button
        onClick={() => setAssetInfoModal(item)}
        style={{
          background: "none", border: "none", cursor: "pointer",
          color: hasSavedInfo ? colors.blue : "#93c5fd",
          padding: "2px 4px", display: "inline-flex", alignItems: "center", gap: "4px",
          fontSize: "12px", fontWeight: "500", minHeight: "auto", transition: "color 0.12s",
        }}
      >
        <LuInfo size={13} /> Asset Info
      </button>
    );
  };

  return (
    <div>
      {/* Topbar */}
      {/* One sticky block, three stacked rows: title + actions, the
          procedures button, then the counts. They have to share a single
          sticky container — separate sticky siblings would each need a
          hand-measured `top`, and this bar's height changes with the button
          row wrapping on mobile. */}
      <div
        style={{
          ...styles.topbar,
          display: "flex",
          flexDirection: "column",
          alignItems: "stretch",
          gap: "10px",
          padding: isMobile ? "12px 16px" : "16px 24px",
          position: "sticky",
          top: 0,
          zIndex: 10,
        }}
      >
        <div
          style={{
            display: "flex",
            gap: "12px",
            flexDirection: isMobile ? "column" : "row",
            alignItems: isMobile ? "flex-start" : "center",
            justifyContent: "space-between",
          }}
        >
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <BackButton onClick={onBack} />
          <div>
            <div
              style={{
                ...styles.pageTitle,
                fontSize: isMobile ? "16px" : "22px",
              }}
            >
              {inspection.site_name}
            </div>
            <div style={{ fontSize: "13px", color: "#666" }}>
              {typeLabel} Inspection — {inspectionDateSection()}
              {!isMobile && <> — {inspection.inspector_name}</>}
            </div>
          </div>
        </div>

        {/* Action buttons */}
        <div
          style={{
            display: "flex",
            gap: "8px",
            alignItems: "center",
            width: isMobile ? "100%" : undefined,
            flexWrap: isMobile ? "wrap" : undefined,
          }}
        >
          {!isCompleted && (() => {
            const actionBtn = (bg) => ({
              backgroundColor: bg,
              color: colors.white,
              border: "none",
              borderRadius: "8px",
              padding: "10px 8px",
              fontSize: "13px",
              fontWeight: "600",
              cursor: "pointer",
              whiteSpace: "nowrap",
              textAlign: "center",
              minHeight: "auto",
              boxSizing: "border-box",
              flex: isMobile ? "1 1 calc(50% - 4px)" : "0 0 120px",
            });
            return (
              <>
                <button
                  onClick={saveAll}
                  disabled={saving}
                  style={{ ...actionBtn(colors.blue), opacity: saving ? 0.6 : 1 }}
                >
                  {saving ? "Saving..." : "Save"}
                </button>
                <button onClick={passAll} style={actionBtn(colors.green)}>
                  Pass All
                </button>
                <button onClick={passRemaining} style={actionBtn(colors.green)}>
                  Pass Rest
                </button>
                <button
                  onClick={completeInspection}
                  disabled={completing}
                  style={{ ...actionBtn(colors.darkGrey), opacity: completing ? 0.6 : 1 }}
                >
                  {completing ? "Completing..." : "Complete"}
                </button>
                <button
                  onClick={openAddAssetModal}
                  disabled={assetGroups.length === 0}
                  style={{ ...actionBtn(colors.teal), opacity: assetGroups.length === 0 ? 0.5 : 1 }}
                >
                  + Add Asset
                </button>
              </>
            );
          })()}
          {isCompleted && (
            <div
              style={{
                display: "flex",
                gap: "8px",
                alignItems: "center",
                width: isMobile ? "100%" : undefined,
              }}
            >
              <span style={styles.statusBadge(inspection.status)}>
                {inspection.status === "passed" ? "Passed" : "Failed"}
              </span>
              <button
                onClick={async () => {
                  const blob = await generateInspectionPDF(inspection, await loadTechSignatures());
                  const filed = await saveInspectionBlobToFolder(inspection, blob, null);
                  if (!filed) setError("This report downloaded, but failed to save a copy to Records. Try again shortly.");
                }}
                style={{
                  backgroundColor: colors.darkGrey,
                  color: colors.white,
                  border: "none",
                  padding: "10px 16px",
                  borderRadius: "6px",
                  fontSize: "14px",
                  fontWeight: "600",
                  cursor: "pointer",
                  flex: isMobile ? 1 : undefined,
                  minHeight: "auto",
                }}
              >
                {isMobile
                  ? "PDF"
                  : inspection.site_large_building
                    ? "Download Full Report"
                    : "Download PDF"}
              </button>
            </div>
          )}
        </div>
        </div>

        {/* Procedures — its own full-width row rather than a sixth button:
            Pass All and Pass Rest are already green, and this is something to
            read, not an action on the inspection. */}
        {procedures.length > 0 && (
          <button
            onClick={() => setViewingProcedure(procedures[0])}
            style={{
              width: "100%",
              backgroundColor: colors.green,
              color: colors.white,
              border: "none",
              borderRadius: "8px",
              padding: "12px 14px",
              fontSize: isMobile ? "14px" : "13px",
              fontWeight: "700",
              cursor: "pointer",
              textAlign: "center",
              minHeight: "auto",
            }}
          >
            ⚡ {procedures.length === 1 ? procedures[0].title : "Site Procedures"}
          </button>
        )}

        {/* Summary bar */}
        <div
          style={{
            display: "flex",
            gap: isMobile ? "6px" : "12px",
            marginBottom: isMobile ? "12px" : "16px",
          }}
        >
          {[
            { key: "total", label: "Total", value: allItems.length, color: "#666" },
            { key: "pass", label: "Passed", value: passCount, color: colors.green },
            { key: "fail", label: "Failed", value: failCount, color: colors.red },
            { key: "not_tested", label: "N/T", value: notTestedCount, color: colors.orange },
            { key: "follow_ups", label: "Follow Ups", value: followUpCount, color: colors.slate },
          ].map(({ key, label, value, color }) => {
            const selected = filter === key;
            return (
            <div
              key={label}
              onClick={() => selectFilter(key)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  selectFilter(key);
                }
              }}
              title={`Show ${label.toLowerCase()}`}
              style={{
                flex: 1,
                backgroundColor: colors.white,
                borderRadius: "8px",
                padding: isMobile ? "10px 6px" : "16px",
                // Selected reads as a ring in the tile's own colour, the same
                // way the result buttons show active.
                boxShadow: selected
                  ? `0 0 0 2px ${color}, 0 1px 3px rgba(0,0,0,0.08)`
                  : "0 1px 3px rgba(0,0,0,0.08)",
                textAlign: "center",
                cursor: "pointer",
                userSelect: "none",
                outline: "none",
                transition: "box-shadow 0.15s",
              }}
            >
              <div
                style={{
                  fontSize: isMobile ? "22px" : "28px",
                  fontWeight: "700",
                  color,
                }}
              >
                {value}
              </div>
              <div
                style={{
                  fontSize: "11px",
                  color: "#999",
                  textTransform: "uppercase",
                  marginTop: "4px",
                }}
              >
                {label}
              </div>
            </div>
            );
          })}
        </div>

      </div>

      <div
        style={{
          ...styles.content,
          padding: isMobile ? "12px" : "24px",
        }}
      >
        {error && (
          <div
            style={{
              backgroundColor: "#fdf0ef",
              border: `1px solid ${colors.red}`,
              borderRadius: "8px",
              padding: "12px 16px",
              marginBottom: "16px",
              color: colors.red,
              fontSize: "14px",
            }}
          >
            {error}
          </div>
        )}

        {/* Inspection items — one flat list, matching the Asset Register tab */}
        {visibleItems.length === 0 ? (
          <div
            style={{
              ...styles.card,
              padding: "40px",
              textAlign: "center",
              color: "#999",
            }}
          >
            {sortedItems.length === 0
              ? "No assets found for this inspection type."
              : EMPTY_FILTER_MESSAGE[filter] || "Nothing to show here."}
          </div>
        ) : (
          <div style={{ ...styles.card, marginBottom: "16px" }}>
            {/* Column header (desktop only — mobile stays stacked) */}
            {!isMobile && (
              <div
                style={{
                  ...styles.tableHeader,
                  gridTemplateColumns: gridCols,
                }}
              >
                <div>#</div>
                {hasUnits && <div>Unit / Tenant</div>}
                <div>Type</div>
                <div>Floor / Group</div>
                <div>Location</div>
                {showCylinder && <div>Cylinder Date</div>}
                <div>Result</div>
              </div>
            )}

        {/* Inspection items */}
            {visibleItems.map((item, idx) => {
                // Fails only. Not Found sits in the grey row with the
                // follow-up buttons and behaves like them: tap and move on.
                // Asking a tech to justify every asset that was not on the
                // wall was slowing the job down for a line the office can act
                // on anyway — the follow-up names the asset and its location.
                const needsNotes = item.result === "fail";
                const failNotes = needsNotes && (
                  <div style={{ marginTop: "10px" }}>
                    {failTypeChecklist(item)}
                    <textarea
                      disabled={isCompleted}
                      value={item.notes}
                      onChange={(e) =>
                        updateItem(item.id, "notes", e.target.value)
                      }
                      onBlur={() => saveItem(item.id)}
                      placeholder="Notes required for failures..."
                      style={{
                        ...inputStyle,
                        height: "60px",
                        resize: "vertical",
                        fontSize: "13px",
                        borderColor: !item.notes.trim()
                          ? colors.red
                          : colors.border,
                        backgroundColor: isCompleted ? "#f9f9f9" : "white",
                      }}
                    />
                    {!item.notes.trim() && (
                      <div
                        style={{
                          fontSize: "11px",
                          color: colors.red,
                          marginTop: "2px",
                        }}
                      >
                        Notes required for failures
                      </div>
                    )}
                  </div>
                );

                return (
                  <div
                    key={item.id}
                    style={{
                      padding: isMobile ? "12px 14px" : "8px 16px",
                      borderBottom: `1px solid ${colors.border}`,
                      backgroundColor:
                        item.result === "fail" && !item.swapped
                          ? "#fff8f8"
                          : item.result === "pass" || item.swapped
                            ? "#f8fff8"
                            : item.result === "not_found"
                              ? "#f6f7f9"
                              : idx % 2 === 0 ? "#fff" : "#fafafa",
                    }}
                  >
                    {isMobile ? (
                      /* Mobile layout — stacked */
                      <div>
                        <div style={{ display: "flex", alignItems: "flex-start", gap: "10px", marginBottom: "10px" }}>
                          <div style={{ color: "#94a3b8", fontSize: "12px", paddingTop: "2px", minWidth: "20px" }}>
                            {item.item_number || idx + 1}
                          </div>
                          <div style={{ flex: 1 }}>
                            {item.unit_label && (
                              <div style={{ fontSize: "12px", color: colors.activeBlue, fontWeight: "700" }}>
                                {item.unit_label}
                              </div>
                            )}
                            <div style={{ fontWeight: "600", fontSize: "15px" }}>
                              {assetTypeSection(item)}
                            </div>
                            <div style={{ fontSize: "13px", color: "#999", marginTop: "2px", display: "flex", gap: "4px", alignItems: "center" }}>
                              {floorLevelSection(item)} · {locationSection(item)}
                            </div>
                            {isExtinguisher(item.asset_type_name) && (
                              <div style={{ marginTop: "4px" }}>{cylinderDateSection(item)}</div>
                            )}
                            {assetInfoButton(item)}
                          </div>
                        </div>
                        <div style={{ display: "flex", gap: "8px", marginBottom: "4px" }}>
                          {resultButtons(item)}
                        </div>
                        {greyButtons(item)}
                        {swapAndGoSection(item)}
                        {failNotes}
                      </div>
                    ) : (
                      /* Desktop layout — dense table row, matching the Asset Register */
                      <div>
                        <div
                          style={{
                            display: "grid",
                            gridTemplateColumns: gridCols,
                            gap: "16px",
                            alignItems: "center",
                            fontSize: "14px",
                          }}
                        >
                          <div style={{ color: "#94a3b8", fontSize: "12px" }}>
                            {item.item_number || idx + 1}
                          </div>
                          {hasUnits && (
                            <div style={{ color: colors.activeBlue, fontWeight: "600" }}>
                              {item.unit_label || "—"}
                            </div>
                          )}
                          <div>
                            <div style={{ color: colors.text }}>{assetTypeSection(item)}</div>
                            {assetInfoButton(item)}
                          </div>
                          <div style={{ color: colors.text }}>{floorLevelSection(item)}</div>
                          <div style={{ color: colors.text }}>{locationSection(item)}</div>
                          {showCylinder && (
                            <div>
                              {cylinderDateSection(item) || (
                                <span style={{ color: "#ccc" }}>—</span>
                              )}
                            </div>
                          )}
                          <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                            <div style={{ display: "flex", gap: "8px" }}>
                              {resultButtons(item)}
                            </div>
                            {greyButtons(item)}
                            {swapAndGoSection(item)}
                          </div>
                        </div>
                        {failNotes}
                      </div>
                    )}

                    {photoSection(item)}
                  </div>
                );
              })}
          </div>
        )}

        {inspection.notes && (
          <div style={styles.card}>
            <div
              style={{
                padding: "16px 20px",
                borderBottom: `1px solid ${colors.border}`,
                fontWeight: "600",
                fontSize: "14px",
              }}
            >
              General Notes
            </div>
            <div style={{ padding: "16px 20px", fontSize: "14px" }}>
              {inspection.notes}
            </div>
          </div>
        )}

        {/* Bottom padding so FAB doesn't cover last item on mobile */}
        {isMobile && <div style={{ height: "80px" }} />}
      </div>

      {showAddAssetModal && (() => {
        const group = assetGroups.find((g) => g.key === addAssetGroupKey);
        const typeOptions = group
          ? assetTypes.filter((t) => t.category === group.categoryId && t.is_active !== false)
          : [];
        const showCylinderField = group && isExtinguisher(group.categoryName);
        return (
          <SimpleModal
            title="Add a Missing Asset"
            onClose={() => setShowAddAssetModal(false)}
            onSave={handleAddAsset}
            saving={addAssetSaving}
            error={addAssetError}
          >
            <div style={{ marginBottom: "14px" }}>
              <label style={{ fontSize: "12px", fontWeight: "600", color: "#64748b", display: "block", marginBottom: "6px", textTransform: "uppercase" }}>
                Category *
              </label>
              <select
                style={{ ...inputStyle }}
                value={addAssetGroupKey}
                onChange={(e) => handleAddAssetGroupChange(e.target.value)}
              >
                {assetGroups.map((g) => (
                  <option key={g.key} value={g.key}>
                    {g.categoryName}{g.unitLabel ? ` — ${g.unitLabel}` : ""}
                  </option>
                ))}
              </select>
            </div>
            <div style={{ marginBottom: "14px" }}>
              <label style={{ fontSize: "12px", fontWeight: "600", color: "#64748b", display: "block", marginBottom: "6px", textTransform: "uppercase" }}>
                Asset Type
              </label>
              <select
                style={{ ...inputStyle }}
                value={addAssetTypeId}
                onChange={(e) => setAddAssetTypeId(e.target.value)}
              >
                <option value="">—</option>
                {typeOptions.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </div>
            <div style={{ marginBottom: "14px" }}>
              <label style={{ fontSize: "12px", fontWeight: "600", color: "#64748b", display: "block", marginBottom: "6px", textTransform: "uppercase" }}>
                Location *
              </label>
              <input
                type="text"
                autoFocus
                style={{ ...inputStyle }}
                value={addAssetLocation}
                onChange={(e) => setAddAssetLocation(e.target.value)}
                placeholder="e.g. Near main entrance"
              />
            </div>
            <div style={{ marginBottom: "14px" }}>
              <label style={{ fontSize: "12px", fontWeight: "600", color: "#64748b", display: "block", marginBottom: "6px", textTransform: "uppercase" }}>
                Floor Level
              </label>
              <input
                type="text"
                style={{ ...inputStyle }}
                value={addAssetFloorLevel}
                onChange={(e) => setAddAssetFloorLevel(e.target.value)}
              />
            </div>
            {showCylinderField && (
              <div style={{ marginBottom: "14px" }}>
                <label style={{ fontSize: "12px", fontWeight: "600", color: "#64748b", display: "block", marginBottom: "6px", textTransform: "uppercase" }}>
                  Cylinder Date
                </label>
                <input
                  type="date"
                  style={{ ...inputStyle }}
                  value={addAssetCylinderDate}
                  onChange={(e) => setAddAssetCylinderDate(e.target.value)}
                />
              </div>
            )}
            <div>
              <label style={{ fontSize: "12px", fontWeight: "600", color: "#64748b", display: "block", marginBottom: "6px", textTransform: "uppercase" }}>
                Item Number
              </label>
              <input
                type="number"
                style={{ ...inputStyle }}
                value={addAssetItemNumber}
                onChange={(e) => setAddAssetItemNumber(e.target.value)}
              />
            </div>
          </SimpleModal>
        );
      })()}

      {swapModal && (
        <SimpleModal
          title="Swap & Go — Record Replacement"
          onClose={() => { setSwapModal(null); setSwapDate(""); setSwapNotes(""); setSwapError(""); }}
          onSave={handleSwapAndGo}
          saving={swapSaving}
          error={swapError}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "16px", color: "#16a34a" }}>
            <TbFireExtinguisher size={20} />
            <span style={{ fontSize: "13px", fontWeight: "600" }}>
              {swapModal.asset_type_name} — {swapModal.location || "No location"}
            </span>
          </div>
          <div style={{ marginBottom: "14px" }}>
            <label style={{ fontSize: "12px", fontWeight: "600", color: "#64748b", display: "block", marginBottom: "6px", textTransform: "uppercase" }}>
              New Cylinder Date *
            </label>
            <input
              type="date"
              style={{ ...inputStyle }}
              value={swapDate}
              onChange={(e) => setSwapDate(e.target.value)}
              autoFocus
            />
          </div>
          <div>
            <label style={{ fontSize: "12px", fontWeight: "600", color: "#64748b", display: "block", marginBottom: "6px", textTransform: "uppercase" }}>
              Notes
            </label>
            <textarea
              style={{ ...inputStyle, height: "72px", resize: "vertical" }}
              value={swapNotes}
              onChange={(e) => setSwapNotes(e.target.value)}
              placeholder="Cylinder 5 yearly swap over done during inspection."
            />
          </div>
        </SimpleModal>
      )}

      {/* Asset Info Modal */}
      {viewingProcedure && (
        <ProcedureViewer
          procedure={viewingProcedure}
          isMobile={isMobile}
          onClose={() => setViewingProcedure(null)}
        />
      )}

      {assetInfoModal && (
        <SimpleModal
          title={`Asset Info — ${assetInfoModal.asset_name || "Asset"}`}
          onClose={() => {
            const assetId = assetInfoModal.asset;
            setAssetInfoModal(null);
            setAssetInfoEdits((prev) => { const n = { ...prev }; delete n[assetId]; return n; });
            setAssetInfoPhotoEdits((prev) => { const n = { ...prev }; delete n[assetId]; return n; });
          }}
          onSave={() => saveAssetInfo(assetInfoModal)}
          saving={assetInfoSaving === assetInfoModal.asset}
          wide
        >
          {(assetInfoModal.asset_info || []).length === 0 ? (
            <p style={{ color: "#94a3b8", fontStyle: "italic", margin: "0 0 12px" }}>
              No info fields defined for this asset type.
            </p>
          ) : (
            (assetInfoModal.asset_info || []).map((v) => {
              const assetId = assetInfoModal.asset;
              const val = assetInfoEdits[assetId]?.[v.field_id] !== undefined
                ? assetInfoEdits[assetId][v.field_id]
                : v.value || "";
              return (
                <div key={v.field_id} style={{ marginBottom: "14px" }}>
                  <label style={{ fontSize: "12px", fontWeight: "600", color: colors.slate, display: "block", marginBottom: "4px" }}>
                    {v.label}
                  </label>
                  <input
                    value={val}
                    onChange={(e) =>
                      setAssetInfoEdits((prev) => ({
                        ...prev,
                        [assetId]: { ...(prev[assetId] || {}), [v.field_id]: e.target.value },
                      }))
                    }
                    placeholder={`Enter ${v.label.toLowerCase()}…`}
                    style={{ ...inputStyle, width: "100%" }}
                  />
                </div>
              );
            })
          )}
          <div style={{ display: "flex", gap: "20px", marginTop: "8px", flexWrap: "wrap" }}>
            {[1, 2].map((slot) => {
              const assetId = assetInfoModal.asset;
              const photos = assetInfoModal.asset_info_photos || [];
              const existing = photos.find((p) => p.slot === slot);
              const pendingFile = assetInfoPhotoEdits[assetId]?.[slot];
              const previewUrl = pendingFile ? URL.createObjectURL(pendingFile) : existing?.url;
              return (
                <div key={slot}>
                  <div style={{ fontSize: "12px", fontWeight: "600", color: colors.slate, marginBottom: "6px" }}>
                    Photo {slot}
                  </div>
                  {previewUrl ? (
                    <img src={previewUrl} alt={`info ${slot}`} style={{
                      width: "100px", height: "100px", objectFit: "cover",
                      borderRadius: "8px", border: `1px solid ${colors.border}`,
                      display: "block", marginBottom: "6px",
                    }} />
                  ) : (
                    <div style={{
                      width: "100px", height: "100px", borderRadius: "8px",
                      border: `2px dashed ${colors.border}`, display: "flex",
                      alignItems: "center", justifyContent: "center",
                      color: "#bbb", fontSize: "12px", marginBottom: "6px",
                    }}>
                      No photo
                    </div>
                  )}
                  <PhotoInput
                    compact
                    takeLabel={previewUrl ? "Retake" : "Take photo"}
                    libraryLabel={previewUrl ? "Replace" : "Library"}
                    onFiles={(files) => {
                      const file = files[0];
                      if (!file) return;
                      setAssetInfoPhotoEdits((prev) => ({
                        ...prev,
                        [assetId]: { ...(prev[assetId] || {}), [slot]: file },
                      }));
                    }}
                  />
                </div>
              );
            })}
          </div>
        </SimpleModal>
      )}
    </div>
  );
}

// What a tech sees when they tap the green button. Read-only throughout —
// there is deliberately no edit affordance here; procedures are written by the
// office on the site's Procedures tab.
function ProcedureViewer({ procedure, isMobile, onClose }) {
  const steps = procedure.steps || [];
  return (
    <>
      <div
        onClick={onClose}
        style={{ position: "fixed", inset: 0, backgroundColor: "rgba(0,0,0,0.45)", zIndex: 399 }}
      />
      <div
        style={{
          position: "fixed",
          zIndex: 400,
          backgroundColor: colors.white,
          display: "flex",
          flexDirection: "column",
          ...(isMobile
            ? { left: 0, right: 0, bottom: 0, top: 0 }
            : {
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                width: "620px",
                maxHeight: "85vh",
                borderRadius: "12px",
                boxShadow: "0 8px 32px rgba(0,0,0,0.2)",
              }),
        }}
      >
        <div
          style={{
            padding: "16px 20px",
            borderBottom: `1px solid ${colors.border}`,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: "12px",
          }}
        >
          <div style={{ fontWeight: "700", fontSize: "17px", color: colors.darkGrey }}>
            {procedure.title}
          </div>
          <button
            onClick={onClose}
            style={{ background: "none", border: "none", fontSize: "22px", cursor: "pointer", color: colors.slate }}
          >
            ✕
          </button>
        </div>

        <div style={{ flex: 1, overflowY: "auto", padding: "16px 20px" }}>
          {procedure.intro && (
            <div
              style={{
                fontSize: "14px",
                color: colors.text,
                backgroundColor: "#f8fafc",
                border: `1px solid ${colors.border}`,
                borderRadius: "8px",
                padding: "12px",
                marginBottom: "16px",
                whiteSpace: "pre-wrap",
              }}
            >
              {procedure.intro}
            </div>
          )}

          {steps.length === 0 ? (
            <div style={{ textAlign: "center", padding: "24px", color: "#aaa", fontSize: "14px" }}>
              No steps have been written yet.
            </div>
          ) : (
            steps.map((step, i) => (
              <div
                key={step.id}
                style={{
                  display: "flex",
                  gap: "12px",
                  paddingBottom: "16px",
                  marginBottom: "16px",
                  borderBottom:
                    i === steps.length - 1 ? "none" : `1px solid ${colors.border}`,
                }}
              >
                <div
                  style={{
                    flexShrink: 0,
                    width: "28px",
                    height: "28px",
                    borderRadius: "50%",
                    backgroundColor: colors.green,
                    color: colors.white,
                    fontSize: "14px",
                    fontWeight: "700",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {i + 1}
                </div>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div
                    style={{
                      fontSize: "15px",
                      color: colors.text,
                      lineHeight: "1.5",
                      whiteSpace: "pre-wrap",
                    }}
                  >
                    {step.text}
                  </div>
                  {step.photo_url && (
                    <img
                      src={step.photo_url}
                      alt=""
                      style={{
                        marginTop: "10px",
                        width: "100%",
                        maxHeight: "320px",
                        objectFit: "contain",
                        borderRadius: "8px",
                        border: `1px solid ${colors.border}`,
                        backgroundColor: "#f8fafc",
                        display: "block",
                      }}
                    />
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </>
  );
}
